import { fetchFieldAppWeatherAt } from "./fieldAppApi";

export const WEATHER_LOOKUP_MAX_MS = 10 * 60 * 1000;

export function weatherTimeMs(value) {
  if (value == null || value === "") return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export function nearestWeatherAt(series, timeMs, maxAgeMs) {
  if (!Array.isArray(series) || !series.length) return null;
  if (!Number.isFinite(timeMs)) {
    return series[series.length - 1];
  }

  let best = series[0];
  let bestDiff = Infinity;
  series.forEach((item) => {
    const itemMs = weatherTimeMs(item && item.time);
    if (itemMs == null) return;
    const diff = Math.abs(itemMs - timeMs);
    if (diff < bestDiff) {
      best = item;
      bestDiff = diff;
    }
  });
  if (
    Number.isFinite(maxAgeMs) &&
    maxAgeMs >= 0 &&
    bestDiff > maxAgeMs
  ) {
    return null;
  }
  return best;
}

export function weatherLoadingHtml(t) {
  const translate = t || function (text) {
    return text;
  };
  return (
    "<div class=\"agri-point-weather agri-point-weather--loading\">" +
    translate("Carregando clima...") +
    "</div>"
  );
}

export function weatherUnavailableHtml(t) {
  const translate = t || function (text) {
    return text;
  };
  return (
    "<div class=\"agri-point-weather agri-point-weather--empty\">" +
    "<div class=\"agri-point-weather__title agri-point-weather__title--muted\">" +
    translate("Clima") +
    "</div>" +
    "<div class=\"agri-point-weather__empty\">" +
    translate("Sem dados de clima neste horário") +
    "</div>" +
    "</div>"
  );
}

export function pointLatLng(point) {
  if (!point) return { lat: null, lng: null };
  const lat = Number(point.lat != null ? point.lat : point.latitude);
  const lng = Number(point.lng != null ? point.lng : point.longitude);
  return {
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
  };
}

export function pickWeatherSeries(tracking, point) {
  const farm = (tracking && tracking.weatherFarm) || [];
  const app =
    (tracking && tracking.weatherApp) || (tracking && tracking.weather) || [];
  if (point && point.inside_landplot === false) {
    return app;
  }
  return farm.length ? farm : app;
}

export function pickWeatherFallbackSeries(tracking, point) {
  const farm = (tracking && tracking.weatherFarm) || [];
  const app =
    (tracking && tracking.weatherApp) || (tracking && tracking.weather) || [];
  if (point && point.inside_landplot === false) {
    return farm;
  }
  return app;
}

export async function loadWeatherInfoWindowHtml({
  farmId,
  point,
  operatorIds = [],
  t,
} = {}) {
  const timeMs = weatherTimeMs(
    point && (point.time || point.timestamp || point.iso)
  );
  if (!farmId || timeMs == null) return "";
  const coords = pointLatLng(point);
  try {
    const weather = await fetchFieldAppWeatherAt(farmId, {
      timestamp: new Date(timeMs).toISOString(),
      operatorIds,
      lat: coords.lat,
      lng: coords.lng,
    });
    if (!weather) return weatherUnavailableHtml(t);
    return weatherInfoWindowHtml(weather, t);
  } catch (error) {
    return weatherUnavailableHtml(t);
  }
}

export function weatherInfoWindowHtml(weather, t) {
  if (!weather) return "";
  const translate = t || function (text) {
    return text;
  };
  const temp = Number.isFinite(Number(weather.temperatureC))
    ? Math.round(Number(weather.temperatureC))
    : null;
  const speed = Number.isFinite(Number(weather.windSpeedKmh))
    ? Math.round(Number(weather.windSpeedKmh))
    : null;
  const humidity = Number.isFinite(Number(weather.humidity))
    ? Math.round(Number(weather.humidity))
    : null;
  if (temp == null && speed == null && humidity == null) return "";

  const deg = Number(weather.windDeg) || 0;
  const flowDeg = (deg + 180) % 360;
  const cardinal = weather.windCardinal || "";

  return (
    "<div class=\"agri-point-weather\">" +
    "<div class=\"agri-point-weather__title\">" +
    translate("Clima") +
    "</div>" +
    "<div class=\"agri-point-weather__body\">" +
    (temp != null
      ? "<div class=\"agri-point-weather__temp\">" + temp + "°</div>"
      : "") +
    "<svg viewBox=\"0 0 64 64\" width=\"20\" height=\"20\" aria-hidden=\"true\" " +
    "class=\"agri-point-weather__arrow\" style=\"transform:rotate(" +
    flowDeg +
    "deg);\">" +
    "<path d=\"M32 6 L44 38 L32 32 L20 38 Z\" fill=\"currentColor\"></path>" +
    "<rect x=\"29.2\" y=\"30\" width=\"5.6\" height=\"26\" rx=\"1.2\" fill=\"currentColor\"></rect>" +
    "</svg>" +
    "<div class=\"agri-point-weather__meta\">" +
    "<div><strong>" +
    translate("Vento") +
    ":</strong> " +
    (speed != null ? speed + " km/h" : "—") +
    (cardinal ? " · " + cardinal : "") +
    "</div>" +
    (humidity != null
      ? "<div><strong>" +
        translate("Umidade do ar") +
        ":</strong> " +
        humidity +
        "%</div>"
      : "") +
    (weather.lastUpdated
      ? "<div class=\"agri-point-weather__updated\">" +
        translate("Última atualização") +
        ": " +
        formatWeatherUpdatedAt(weather.lastUpdated) +
        "</div>"
      : "") +
    "</div></div></div>"
  );
}

function formatWeatherUpdatedAt(value) {
  const ms = weatherTimeMs(value);
  if (ms == null) return "—";
  return new Date(ms).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function mergeWeatherSeries(existing, incoming) {
  const merged = [...(existing || []), ...(incoming || [])];
  const seen = new Set();
  return merged
    .filter((item) => item && item.time)
    .sort((left, right) => String(left.time).localeCompare(String(right.time)))
    .filter((item) => {
      const key = `${item.time}|${item.temperatureC}|${item.windSpeedKmh}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}
